"use client";

import React from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";

const projects = [
    {
        slug: "yapmarket",
        name: "Yap.market",
        role: "Built for growth from day one.",
        outcome: "Scaled to 10k+ users",
        image: "/work/yapmarket/yapmarket.png",
        details: ["Optimized referral system", "High retention loops", "10k+ users scaled"]
    },
    {
        slug: "pp-editor",
        name: "PP Editor",
        role: "AI-Powered Video Editing.",
        outcome: "10x Faster Workflow",
        image: "/work/ppeditor/ppeditor.png",
        details: ["AI Workflows", "Auto-Captioning", "Lightning Fast Export"]
    },
    {
        slug: "astra-finance",
        name: "Astra Finance",
        role: "Security defined by intent.",
        outcome: "$50M+ TVL secured",
        image: "/about1.webp",
        details: ["StarkNet Integration", "Audit-ready codebase", "Institutional-grade UX"]
    },
    {
        slug: "echo-ai",
        name: "Echo AI",
        role: "Execution over noise.",
        outcome: "30% efficiency gain",
        image: "/about2.webp",
        details: ["Specialized LLM", "Workflow Automation", "Orchestration Pipeline"]
    }
];

export const WorkSection = () => {
    return (
        <section className="relative py-40 px-4 bg-white text-black overflow-hidden">
            <div className="max-w-[1400px] mx-auto space-y-32">
                <div className="flex flex-col md:flex-row justify-between items-baseline gap-8">
                    <h2 className="font-display text-8xl md:text-9xl uppercase leading-tight">
                        Our Work.
                    </h2>
                    <p className="font-sans text-xl text-black/40 uppercase tracking-widest">
                        Selected Cases (2024-2025)
                    </p>
                </div>

                <div className="space-y-40">
                    {projects.map((project, index) => (
                        <Link 
                            key={project.slug} 
                            href={`/viewwork/${project.slug}`}
                            className="block group"
                        >
                            <motion.div
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-10%" }}
                                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                                className="relative"
                                data-cursor="work"
                            >
                                <div className="aspect-[16/9] overflow-hidden rounded-sm bg-black/5 relative">
                                    <Image
                                        src={project.image}
                                        alt={project.name}
                                        fill
                                        className="object-cover group-hover:scale-105 transition-transform duration-1000 ease-out grayscale group-hover:grayscale-0"
                                    />
                                    <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-700" />
                                </div>

                                <div className="mt-12 flex flex-col md:flex-row justify-between items-start gap-8">
                                    <div className="space-y-4">
                                        <h3 className="font-display text-5xl md:text-6xl uppercase tracking-tighter">{project.name}</h3>
                                        <p className="font-sans text-2xl text-black/60 uppercase">{project.role}</p>
                                        <ul className="flex flex-wrap gap-4 pt-4">
                                            {project.details?.map(detail => (
                                                <li key={detail} className="micro-id tracking-widest text-black/60 border border-black/10 px-4 py-2 rounded-full !text-black/60">
                                                    {detail}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="p-4 border-2 border-black rounded-full px-8 group-hover:bg-black group-hover:text-white transition-all duration-500 cursor-default">
                                        <span className="font-display text-3xl uppercase tracking-tighter">{project.outcome}</span>
                                    </div>
                                </div>
                            </motion.div>
                        </Link>
                    ))}
                </div>

                {/* Explore More Button */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="pt-20"
                >
                    <Link
                        href="/case-studies"
                        className="group inline-flex items-center gap-8"
                    >
                        <div className="relative">
                            <span className="font-display text-4xl md:text-5xl uppercase tracking-tighter group-hover:text-primary transition-colors duration-500">
                                Explore <br /> All Case Studies
                            </span>
                            <div className="absolute -bottom-2 left-0 w-full h-[1px] bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                        </div>
                        <div className="w-20 h-20 rounded-full border border-black/10 flex items-center justify-center group-hover:bg-primary group-hover:border-primary transition-all duration-500 -rotate-45 group-hover:rotate-0">
                            <svg className="w-8 h-8 transition-transform duration-500 group-hover:text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </div>
                    </Link>
                </motion.div>
            </div>

            {/* Background premium detail */}
            <div className="bg-noise opacity-5 invert pointer-events-none" />
        </section>
    );
};
